import { window } from "global";
import { useState, useEffect, useRef } from "react";
import classnames from "classnames";
import { MediaQuery } from "@outplayed/responsive";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { Item } from "@outplayed/tooltips";
import { PrismaticItemOption } from "@ugg/shared/api/data-parser/champions/overview";
import { numberWithCommas } from "@ugg/shared/utils/math";
import { getArenaPrismaticWinRateColor } from "@ugg/shared/utils/win-rates";

interface ArenaPrismaticItemsProps {
  className?: string;
  championId: number;
  items: PrismaticItemOption[];
  patch: string;
}

export function ArenaPrismaticItems(props: ArenaPrismaticItemsProps) {
  const { getChampionName } = getRiotAssetsContext();
  const { className, championId, items, patch } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollPadding, setScrollPadding] = useState(8);

  useEffect(() => {
    const resizeHandler = (event?: Event) => {
      if ((scrollRef.current?.clientWidth || 0) >= (scrollRef.current?.scrollWidth || 0)) {
        setScrollPadding(0);
      } else {
        setScrollPadding(8);
      }
    };

    resizeHandler();
    window.addEventListener("resize", resizeHandler);

    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, [items]);

  const championName = getChampionName(championId);
  const classNames = classnames("content-section content-section_no-padding", className);

  if (items.length === 0) {
    return null;
  }

  // const sortedItems = items.sort((a, b) => b.matches - a.matches);
  const sortedItems = items.sort((a, b) => b.top4Rate - a.top4Rate);

  return (
    <div className={classNames}>
      <div className="content-section_content !bg-transparent">
        <div className="content-section_header">
          <div className="main-header">
            <span>{championName} Prismatic Items</span>
            <MediaQuery min="TABLET" max="DESKTOP_LARGE">
              <span className="content-section_blurb ml-[12px]">These are the best Prismatic items to buy on {championName}</span>
            </MediaQuery>
          </div>
        </div>
        <div
          className="flex gap-[12px] overflow-auto"
          style={{ paddingBottom: scrollPadding, marginBottom: scrollPadding > 0 ? -6 : 0 }}
          ref={scrollRef}
        >
          {sortedItems.map((item) => (
            <PrismaticItem key={item.id} data={item} patch={patch} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PrismaticItem({ data, patch }: { data: PrismaticItemOption; patch: string }) {
  const { useRiotItems } = getRiotAssetsContext();
  const { data: riotItems } = useRiotItems({ itemId: data.id, patch, ssr: true });
  const { name } = riotItems?.[data.id] || {};
  const { top4Rate, pickRate, matches } = data;

  return (
    <div className="flex flex-col flex-none items-center p-[12px] w-[84px] h-[113px] rounded-[3px] bg-purple-200">
      <div className="flex-none p-[1px] w-[30px] h-[30px] rounded-[2px] bg-[linear-gradient(135deg,_#e4fc80,_#83b3ff,_#ca76ff)]">
        <Item className="size-full" itemId={data.id} />
      </div>
      <div className="mt-[6px] w-full text-center">
        <div className="truncate font-semibold text-[11px] text-white empty:before:content-['\200b']">{name}</div>
      </div>
      <hr className="my-[6px] w-full border-accent-purple-700" />
      <div className="text-center">
        <div className="font-semibold text-[11px] text-white" style={{ color: getArenaPrismaticWinRateColor(top4Rate) }}>
          {top4Rate}%
        </div>
        <div className="text-accent-gray-100 text-[11px]">{numberWithCommas(matches)}</div>
      </div>
    </div>
  );
}
